import {responseVersion} from '../lib/http-version.js';
import {CONTROLS} from '../lib/permission-catalog.mjs';

const nativeFetch = window.fetch.bind(window);
const versions = new Map();
const versionKey = url => /\/(review|rfis|annotations|workspace|viewpoints)$/.test(url.pathname)
  ? `${url.pathname.replace(/\/rfis$/, '/review')}?engine=${url.searchParams.get('engine') || 'oda'}`
  : url.pathname + url.search;

let session;
let accountInfo;
let manageSeq = 0;
let accountSeq = 0;
let auditSeq = 0;

const element = (tag, text, parent, attrs = {}) => {
  const node = document.createElement(tag);
  if (text != null) node.textContent = text;
  Object.assign(node, attrs);
  parent?.append(node);
  return node;
};

const button = (text, parent, fn) => {
  const b = element('button', text, parent, {type: 'button', className: 'btn'});
  b.onclick = fn;
  return b;
};

function field(form, label, type = 'text', value = '') {
  const wrap = element('label', label, form);
  const input = element('input', null, wrap, {type, value, required: true});
  return input;
}

const select = (parent, label, values) => {
  const wrap = element('label', label, parent);
  const s = element('select', null, wrap);
  s.setAttribute('aria-label', label);
  for (const [value, text] of values) {
    element('option', text, s, {value});
  }
  return s;
};

async function api(path, data) {
  const response = await nativeFetch(path, {
    method: data ? 'POST' : 'GET',
    headers: {
      'Content-Type': 'application/json',
      'X-DCMS-Request': '1',
      ...(session ? {'X-CSRF-Token': session.csrf} : {})
    },
    ...(data ? {body: JSON.stringify(data)} : {})
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || `HTTP ${response.status}`);
  return result;
}

const AUDIT_ACTION_LABELS = {
  'login.success': '登入成功',
  'auth.login.failed': '登入失敗',
  'auth.login.blocked': '拒絕已鎖定的登入位址',
  'auth.login.cooldown': '帳號登入暫停',
  'auth.login.address_unlock': '解鎖登入位址',
  'logout': '使用者登出',
  'setup.success': '初始設定',
  'user.create': '新增帳號',
  'user.update': '修改帳號',
  'user.delete': '刪除帳號',
  'role.save': '保存角色',
  'role.delete': '刪除角色',
  'binding.create': '新增角色指派',
  'binding.delete': '移除角色指派',
  'review.change': '釋疑變更',
  'rfi.create': '新增釋疑',
  'rfi.reply': '新增釋疑回覆',
  'rfi.update': '更新釋疑',
  'rfi.delete': '刪除釋疑',
  'annotation.create': '新增標註',
  'annotation.update': '更新標註',
  'annotation.delete': '刪除標註',
  'viewpoint.create': '新增視點書籤',
  'viewpoint.update': '更新視點書籤',
  'viewpoint.delete': '刪除視點書籤',
  'library.upload': '上傳文件',
  'library.clear': '清空圖庫'
};

async function login() {
  const status = await api('/api/auth/status');
  return new Promise(resolve => {
    const overlay = element('div', null, document.body, {className: 'auth-overlay'});
    const layout = element('main', null, overlay, {className: 'auth-login-layout'});
    const form = element('form', null, layout, {className: 'auth-card'});

    const brand = element('div', null, form, {className: 'auth-brand-head'});
    element('img', null, brand, {src: './assets/dcms-logo.png', alt: '', className: 'auth-login-logo'});
    const brandText = element('div', null, brand, {className: 'auth-login-brand-text'});
    element('span', 'DCMS', brandText, {className: 'auth-login-wordmark'});
    element('p', 'Drawing Collaboration Management System', brandText, {className: 'auth-login-subtitle'});
    element('span', '圖說協作管理系統', form, {className: 'auth-login-eyebrow'});

    element('h2', status.setupRequired ? '設定管理者密碼' : '讓協作，\n從這裡開始。', form);
    element('p', status.setupRequired
      ? '請使用伺服器 library/auth/setup-code 檔案內的一次性設定碼。帳號固定為 admin。'
      : '歡迎回來，登入以接續您的專案。', form, {className: 'auth-login-desc'});

    const username = field(form, '帳號', 'text', status.setupRequired ? 'admin' : '');
    username.autocomplete = 'username';
    username.readOnly = status.setupRequired;

    const code = status.setupRequired ? field(form, '一次性設定碼') : null;
    const passwordField = element('div', null, form, {className: 'auth-password-field'});
    const passwordLabel = element('label', '密碼', passwordField);
    passwordLabel.htmlFor = 'auth-login-password';
    const passwordWrap = element('span', null, passwordField, {className: 'auth-password-wrap'});
    const password = element('input', null, passwordWrap, {
      id: 'auth-login-password', type: 'password', required: true
    });
    password.autocomplete = status.setupRequired ? 'new-password' : 'current-password';
    password.minLength = status.setupRequired ? 6 : 1;
    password.maxLength = 256;
    const visibility = element('button', null, passwordWrap, {
      type: 'button', className: 'auth-password-toggle', title: '顯示密碼'
    });
    visibility.setAttribute('aria-label', '顯示密碼');
    visibility.setAttribute('aria-pressed', 'false');
    visibility.innerHTML = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6S2 12 2 12Z"/><circle cx="12" cy="12" r="3"/><path class="auth-password-slash" d="M3 3l18 18"/></svg>';
    visibility.onclick = () => {
      const visible = password.type === 'password';
      password.type = visible ? 'text' : 'password';
      visibility.setAttribute('aria-pressed', String(visible));
      visibility.setAttribute('aria-label', visible ? '隱藏密碼' : '顯示密碼');
      visibility.title = visible ? '隱藏密碼' : '顯示密碼';
    };

    const error = element('p', '', form, {className: 'auth-error auth-msg-error'});
    error.setAttribute('role', 'alert');

    const submit = element('button', status.setupRequired ? '建立 admin' : '登入', form, {
      type: 'submit',
      className: 'btn btn-primary'
    });
    const scene = element('aside', null, layout, {className: 'auth-login-scene'});
    scene.setAttribute('aria-label', '建築模型與數位圖說');
    element('span', 'BUILD ON THE RIGHT VERSION', scene, {className: 'auth-scene-tag'});
    const sceneCopy = element('div', null, scene, {className: 'auth-scene-copy'});
    element('span', '每一份圖說，串起每一步。', sceneCopy, {className: 'auth-scene-title'});
    element('p', '文件・釋疑・協作', sceneCopy);

    form.onsubmit = async event => {
      event.preventDefault();
      submit.disabled = true;
      error.textContent = '';
      error.style.display = 'none';
      try {
        if (status.setupRequired) {
          await api('/api/auth/setup', {code: code.value, password: password.value});
        }
        session = await api('/api/auth/login', {username: username.value, password: password.value});
        overlay.remove();
        resolve(session);
      } catch (e) {
        error.textContent = e.message;
        error.style.display = 'block';
        submit.disabled = false;
      }
    };
    (code || username).focus();
  });
}

/** XHR uploads need the same write verification as fetch; keep CSRF in this module. */
export function setUploadAuthHeaders(xhr) {
  if(!session?.csrf)throw new Error('請先登入後再上傳');
  xhr.setRequestHeader('X-DCMS-Request','1');
  xhr.setRequestHeader('X-CSRF-Token',session.csrf);
}

export function can(action, resource = null, owner = null) {
  if (!session) return false;
  return session.bindings.some(binding => {
    const s = binding.scope;
    let matches = s.type === 'global';
    if (resource && s.type === 'unassigned') matches = !resource.projectId;
    if (resource && s.type === 'project') matches = s.id === resource.projectId;
    if (resource && s.type === 'folder') {
      let folder = resource.folderId;
      const seen = new Set();
      while (folder && !seen.has(folder)) {
        if (folder === s.id) {
          matches = true;
          break;
        }
        seen.add(folder);
        folder = window.__dwgv3?.state.folders.find(f => f.id === folder)?.parentId;
      }
    }
    return matches && binding.permissions.some(p =>
      (p.action === action || p.action === '*') &&
      (p.ownership !== 'own' || (!!owner && owner === session.user.id))
    );
  });
}

export async function initAuth() {
  const response = await nativeFetch('/api/auth/session');
  session = response.ok ? await response.json() : await login();
  window.dcmsAuth = {
    can, user: session.user, admin: !!session.admin,
    async refresh(){
      const response=await nativeFetch('/api/auth/session',{cache:'no-store'});
      if(!response.ok)throw new Error('無法更新登入權限');
      const info=await response.json();session=info;
      this.user=info.user;this.admin=!!info.admin;
      return info;
    },
    openAccount: () => openMyAccount().catch(e => alert(e.message)),
    openAccounts: () => manage().catch(e => alert(e.message)),
    openAudit: () => openAudit().catch(e => alert(e.message)),
  };

  window.fetch = async (input, options = {}) => {
    const url = new URL(input instanceof Request ? input.url : String(input), location.href);
    if (url.origin !== location.origin || !url.pathname.startsWith('/api/')) return nativeFetch(input, options);
    const method = (options.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const headers = new Headers(input instanceof Request ? input.headers : undefined);
    new Headers(options.headers).forEach((v, k) => headers.set(k, v));

    if (!['GET', 'HEAD'].includes(method)) {
      headers.set('X-DCMS-Request', '1');
      headers.set('X-CSRF-Token', session.csrf);
      const version = versions.get(versionKey(url));
      if (version && !headers.has('If-Match')) headers.set('If-Match', version);
    }

    const result = await nativeFetch(input, {...options, headers});
    if (result.ok && result.headers.has('ETag')) versions.set(versionKey(url), responseVersion(result));
    if (result.status === 401) {
      location.reload();
      throw new Error('登入已失效，請重新登入');
    }
    if (!result.ok && !['GET', 'HEAD'].includes(method)) {
      const data = await result.clone().json().catch(() => ({}));
      const error = new Error(data.error || `操作失敗：HTTP ${result.status}`);
      error.status = result.status;
      throw error;
    }
    return result;
  };

  mountAccountMenu();
  const accountSettings = document.getElementById('accountSettings');
  const mine = button('我的帳號', accountSettings, () => document.dispatchEvent(new CustomEvent('dcms-settings-pane', {detail: 'mine'})));
  mine.id = 'myAccountJump';
  mine.className = 'side-action';
  if (session.admin) document.getElementById('adminSettings').hidden = false;
  document.documentElement.classList.remove('auth-pending');
}

function confirmLogout() {
  const dialog = element('dialog', null, document.body, {className: 'nav-logout-dialog'});
  dialog.setAttribute('aria-labelledby', 'navLogoutTitle');
  element('h2', '登出', dialog, {id: 'navLogoutTitle'});
  element('p', '確定要登出目前的帳號？', dialog);
  const actions = element('div', null, dialog, {className: 'nav-logout-actions'});
  const cancel = button('取消', actions, () => dialog.close());
  const ok = button('登出', actions, async () => {
    ok.disabled = true;
    cancel.disabled = true;
    try {
      await api('/api/auth/logout', {});
      location.reload();
    } catch (e) {
      ok.disabled = false;
      cancel.disabled = false;
      alert(e.message);
    }
  });
  ok.classList.add('btn-primary');
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });
  dialog.onclose = () => dialog.remove();
  dialog.showModal();
  cancel.focus();
}

function accountInitial(name) {
  const text = String(name || '').trim();
  const ch = [...text][0] || '?';
  return /[a-z]/i.test(ch) ? ch.toUpperCase() : ch;
}

function syncAccountIdentity(name) {
  for (const node of document.querySelectorAll('.nav-account-menu .auth-identity, .nav-account-name')) node.textContent = name;
  const avatar = document.querySelector('.nav-account-avatar');
  if (avatar) avatar.textContent = accountInitial(name);
  const trigger = document.querySelector('.nav-account-trigger');
  if (trigger) trigger.setAttribute('aria-label', `登入者 ${name}`);
}

function mountAccountMenu() {
  const host = document.getElementById('navAccount');
  if (!host) return;
  const user = session.user;
  const trigger = element('button', null, host, {type: 'button', className: 'nav-account-trigger'});
  trigger.setAttribute('aria-haspopup', 'menu');
  trigger.setAttribute('aria-expanded', 'false');
  trigger.setAttribute('aria-controls', 'navAccountMenu');
  element('span', accountInitial(user.displayName), trigger, {className: 'nav-account-avatar', ariaHidden: 'true'});
  element('span', user.displayName, trigger, {className: 'nav-account-name auth-identity'});
  const menu = element('div', null, document.body, {id: 'navAccountMenu', className: 'nav-account-menu', hidden: true});
  menu.setAttribute('role', 'menu');
  const who = element('p', null, menu, {className: 'nav-account-who'});
  element('span', '登入者', who, {className: 'nav-account-kicker'});
  element('strong', user.displayName, who, {className: 'auth-identity'});
  element('span', `@${user.username}`, who, {className: 'nav-account-user'});
  const settings = element('button', null, menu, {id:'accountSettingsEntry',type:'button',className:'nav-account-logout nav-account-settings'});
  settings.setAttribute('role','menuitem');
  settings.dataset.workspace = 'engine';
  const settingsIcon = document.createElementNS('http://www.w3.org/2000/svg','svg');
  settingsIcon.setAttribute('viewBox','0 0 24 24'); settingsIcon.setAttribute('aria-hidden','true');
  const settingsPath = document.createElementNS('http://www.w3.org/2000/svg','path');
  settingsPath.setAttribute('d','M4 6h16M4 12h16M4 18h16M8 3v6M16 9v6M10 15v6');
  settingsIcon.append(settingsPath); settings.append(settingsIcon,element('span','設定'));
  const logout = element('button', null, menu, {type: 'button', className: 'nav-account-logout'});
  logout.setAttribute('role', 'menuitem');
  const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  icon.setAttribute('viewBox', '0 0 24 24');
  icon.setAttribute('aria-hidden', 'true');
  for (const d of ['M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4', 'M16 17l5-5-5-5', 'M21 12H9']) {
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    icon.append(path);
  }
  logout.append(icon, element('span', '登出'));
  const placeMenu = () => {
    const rect = trigger.getBoundingClientRect();
    menu.style.left = `${Math.round(rect.right + 8)}px`;
    menu.style.bottom = `${Math.round(Math.max(8, window.innerHeight - rect.bottom))}px`;
  };
  const setOpen = (open) => {
    menu.hidden = !open;
    trigger.setAttribute('aria-expanded', open ? 'true' : 'false');
    if (!open) return;
    placeMenu();
    settings.focus();
  };
  settings.onclick = () => setOpen(false);
  logout.onclick = () => {
    setOpen(false);
    confirmLogout();
  };
  trigger.onclick = () => setOpen(menu.hidden);
  menu.addEventListener('keydown', event => {
    if (!['ArrowDown','ArrowUp','Home','End'].includes(event.key)) return;
    event.preventDefault();
    const items = [settings,logout], current = items.indexOf(document.activeElement);
    const next = event.key==='Home'?0:event.key==='End'?1:(current+(event.key==='ArrowDown'?1:-1)+items.length)%items.length;
    items[next].focus();
  });
  document.addEventListener('click', (event) => {
    if (menu.hidden || host.contains(event.target) || menu.contains(event.target)) return;
    setOpen(false);
  });
  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && !menu.hidden) {
      setOpen(false);
      trigger.focus();
    }
  });
  window.addEventListener('resize', () => { if (!menu.hidden) placeMenu(); });
  syncAccountIdentity(user.displayName);
}

function assignmentPlace(scope, tree) {
  if (scope?.type === 'global') return {project: '全站', detail: ''};
  if (scope?.type === 'unassigned') return {project: '未分類', detail: ''};
  if (scope?.type === 'project') {
    return {project: tree.projects?.find(p => p.id === scope.id)?.name || '專案已不存在', detail: ''};
  }
  if (scope?.type === 'folder') {
    const folder = tree.folders?.find(f => f.id === scope.id);
    const project = tree.projects?.find(p => p.id === folder?.projectId);
    return {project: project?.name || '專案已不存在', detail: folder?.name ? `目錄：${folder.name}` : '目錄'};
  }
  return {project: '未指定', detail: ''};
}

function formatWhen(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '' : date.toLocaleString();
}

function renderMyAccount(content, {setStatus, tree = {projects: [], folders: []}}) {
  content.replaceChildren();
  const u = session.user;

  const profileCard = element('div', null, content, {className: 'auth-section-card auth-myaccount-profile'});
  const avatarRow = element('div', null, profileCard, {className: 'auth-myaccount-header'});
  const avatar = element('div', (u.displayName || u.username).charAt(0).toUpperCase(), avatarRow, {className: 'auth-user-avatar auth-myaccount-avatar'});
  if (session.admin) avatar.classList.add('is-admin');
  const infoCol = element('div', null, avatarRow, {className: 'auth-myaccount-info'});
  element('h3', u.displayName, infoCol, {className: 'auth-myaccount-name'});
  element('span', `@${u.username}`, infoCol, {className: 'auth-myaccount-username'});
  const badges = element('div', null, infoCol, {className: 'auth-user-tags'});
  if (session.admin) element('span', '系統管理者', badges, {className: 'auth-tag admin'});

  const grantCard = element('div', null, content, {className: 'auth-section-card'});
  element('h3', '授予的角色', grantCard);
  element('p', '這裡列出這個帳號被授予的角色代號，以及該角色指定生效的專案。', grantCard, {className: 'auth-form-desc'});
  const grants = session.bindings || [];
  if (!grants.length) {
    element('p', '尚未授予角色或專案。', grantCard, {className: 'auth-form-desc'});
  } else {
    const list = element('div', null, grantCard, {className: 'auth-grant-list'});
    for (const binding of grants) {
      const place = assignmentPlace(binding.scope, tree);
      const row = element('div', null, list, {className: 'auth-grant-row'});
      const roleCol = element('div', null, row, {className: 'auth-grant-col'});
      element('span', '角色代號', roleCol, {className: 'auth-grant-label'});
      element('strong', binding.roleName || binding.roleId, roleCol);
      if (binding.roleId && !/^[a-f0-9]{32}$/.test(binding.roleId) && binding.roleId !== binding.roleName) {
        element('code', binding.roleId, roleCol, {className: 'auth-grant-code'});
      }
      const projectCol = element('div', null, row, {className: 'auth-grant-col'});
      element('span', '指定專案', projectCol, {className: 'auth-grant-label'});
      element('strong', place.project, projectCol);
      if (place.detail) element('span', place.detail, projectCol, {className: 'auth-grant-detail'});
    }
  }

  const nameCard = element('div', null, content, {className: 'auth-section-card'});
  const nameForm = element('form', null, nameCard, {className: 'auth-form'});
  element('h3', '變更顯示名稱', nameForm);
  element('p', '修改後會立即反映在系統中顯示的名稱。', nameForm, {className: 'auth-form-desc'});
  const nameInput = field(nameForm, '顯示名稱', 'text', u.displayName);
  nameInput.maxLength = 80;
  const nameBtnRow = element('div', null, nameForm, {className: 'auth-form-btn-row'});
  element('button', '保存名稱', nameBtnRow, {type: 'submit', className: 'btn btn-primary'});
  const nameHint = element('p', '', nameForm, {className: 'auth-error'});
  nameForm.onsubmit = async e => {
    e.preventDefault();
    const newName = nameInput.value.trim();
    if (!newName) { nameHint.className = 'auth-error auth-msg-error'; nameHint.textContent = '顯示名稱不可為空'; return; }
    try {
      setStatus('busy', '保存中…');
      const result = await api('/api/auth/profile', {displayName: newName});
      session.user.displayName = result.user.displayName;
      syncAccountIdentity(result.user.displayName);
      setStatus('success', '顯示名稱已更新');
      renderMyAccount(content, {setStatus, tree});
    } catch (err) {
      setStatus('error', err.message);
      nameHint.className = 'auth-error auth-msg-error';
      nameHint.textContent = err.message;
    }
  };

  const contactCard = element('section', null, content, {className:'auth-section-card'});
  const contactForm = element('form', null, contactCard, {className:'auth-form auth-contact-form'});
  element('h3', '聯絡與工作資料', contactForm);
  element('p', '供本人維護聯絡資訊；Email 尚未驗證，不會變更登入帳號或專案權限。', contactForm, {className:'auth-form-desc'});
  const contactGrid = element('div', null, contactForm, {className:'auth-form-grid'});
  const inputs = {};
  for (const [key,label,type,max] of [['email','聯絡 Email','email',254],['phone','聯絡電話','tel',40],['organization','公司／組織','text',120],['department','部門','text',120],['jobTitle','職稱','text',120]]) {
    const input = field(contactGrid,label,type,accountInfo?.profile?.[key] || '');
    input.required = false; input.maxLength = max; input.name = key;
    input.autocomplete = {email:'email',phone:'tel',organization:'organization',jobTitle:'organization-title'}[key] || 'off';
    inputs[key] = input;
  }
  const contactActions = element('div',null,contactForm,{className:'auth-form-btn-row'});
  const contactSave = element('button','保存個人資料',contactActions,{type:'submit',className:'btn btn-primary'});
  const contactHint = element('p','',contactForm,{className:'auth-error'});
  contactHint.setAttribute('role','status');
  contactForm.onsubmit = async event => {
    event.preventDefault(); contactSave.disabled = true;
    try {
      setStatus('busy','保存中…');
      const result = await api('/api/auth/profile',{profile:Object.fromEntries(Object.entries(inputs).map(([key,input])=>[key,input.value]))});
      accountInfo.profile = result.profile;
      for(const [key,input] of Object.entries(inputs)) input.value = result.profile[key] || '';
      contactHint.textContent = '個人資料已保存'; contactHint.className = 'auth-error auth-msg-success';
      setStatus('success','個人資料已更新');
    } catch(err) { contactHint.textContent = err.message; contactHint.className = 'auth-error auth-msg-error'; setStatus('error',err.message); }
    finally { contactSave.disabled = false; }
  };

  const methodsCard = element('section',null,content,{className:'auth-section-card auth-login-methods'});
  element('h3','登入方式',methodsCard);
  element('p','目前使用 DCMS 帳號登入。外部登入開通後，可保留原有專案與協作紀錄。',methodsCard,{className:'auth-form-desc'});
  for(const method of accountInfo?.loginMethods || []) {
    const row = element('div',null,methodsCard,{className:'auth-login-method'});
    const info = element('div',null,row);
    element('strong',method.name,info);
    element('p',method.provider==='local'?'使用系統管理者建立的帳號與密碼。':'尚未開通；由管理者完成整合設定後提供連結。',info,{className:'auth-form-desc'});
    element('span',method.available?'使用中':'尚未開通',row,{className:'auth-tag'+(method.available?' active':'')});
  }

  const pwCard = element('div', null, content, {className: 'auth-section-card'});
  const pwForm = element('form', null, pwCard, {className: 'auth-form'});
  element('h3', '變更密碼', pwForm);
  element('p', '新密碼需為 6 至 256 字元。變更後只保留目前這筆登入，其他連線會被撤銷。', pwForm, {className: 'auth-form-desc'});
  const pwGrid = element('div', null, pwForm, {className: 'auth-form-grid'});
  const newPw = field(pwGrid, '新密碼', 'password');
  newPw.minLength = 6; newPw.maxLength = 256; newPw.autocomplete = 'new-password';
  const confirmPw = field(pwGrid, '確認新密碼', 'password');
  confirmPw.minLength = 6; confirmPw.maxLength = 256; confirmPw.autocomplete = 'new-password';
  const pwBtnRow = element('div', null, pwForm, {className: 'auth-form-btn-row'});
  element('button', '變更密碼', pwBtnRow, {type: 'submit', className: 'btn btn-primary'});
  const pwHint = element('p', '', pwForm, {className: 'auth-error'});
  pwForm.onsubmit = async e => {
    e.preventDefault();
    if (newPw.value !== confirmPw.value) {
      pwHint.className = 'auth-error auth-msg-error';
      pwHint.textContent = '兩次輸入的密碼不一致';
      return;
    }
    try {
      setStatus('busy', '保存中…');
      await api('/api/auth/profile', {password: newPw.value});
      newPw.value = ''; confirmPw.value = '';
      setStatus('success', '密碼已變更，其他裝置的登入已撤銷。');
      pwHint.className = 'auth-error auth-msg-success';
      pwHint.textContent = '密碼已成功變更';
      await refreshSessions();
    } catch (err) {
      setStatus('error', err.message);
      pwHint.className = 'auth-error auth-msg-error';
      pwHint.textContent = err.message;
    }
  };

  const sessionCard = element('div', null, content, {className: 'auth-section-card'});
  element('h3', '登入連線', sessionCard);
  element('p', '每筆登入自建立起 8 小時後到期。可單獨撤銷其他裝置，不影響目前這筆連線。', sessionCard, {className: 'auth-form-desc'});
  const sessionList = element('div', null, sessionCard, {className: 'auth-session-list'});
  const sessionActions = element('div', null, sessionCard, {className: 'auth-form-btn-row'});
  const revokeOthers = button('撤銷其他連線', sessionActions, async () => {
    if (!confirm('撤銷這筆以外的所有登入連線？那些裝置需要重新登入。')) return;
    try {
      setStatus('busy', '撤銷中…');
      const result = await api('/api/auth/sessions/revoke-others', {});
      setStatus('success', result.revoked ? `已撤銷 ${result.revoked} 筆其他登入` : '沒有其他登入連線');
      await refreshSessions();
    } catch (err) {
      setStatus('error', err.message);
    }
  });
  revokeOthers.className = 'btn btn-sm';

  async function refreshSessions() {
    const result = await api('/api/auth/sessions');
    const sessions = result.sessions || [];
    revokeOthers.disabled = sessions.filter(item => !item.current).length === 0;
    sessionList.replaceChildren();
    for (const item of sessions) {
      const row = element('div', null, sessionList, {className: `auth-session-row${item.current ? ' is-current' : ''}`});
      const info = element('div', null, row, {className: 'auth-session-info'});
      const title = element('div', null, info, {className: 'auth-session-title'});
      element('strong', item.current ? '目前連線' : '其他連線', title);
      if (item.current) element('span', '這台裝置', title, {className: 'auth-tag'});
      element('span', [item.client, item.address].filter(Boolean).join(' · '), info, {className: 'auth-session-meta'});
      const dates = element('div', null, info, {className: 'auth-session-meta auth-session-dates'});
      element('span', `建立 ${formatWhen(item.createdAt)}`, dates);
      element('span', `到期 ${formatWhen(item.expiresAt)}`, dates);
      if (!item.current) {
        const revoke = button('撤銷', row, async () => {
          if (!confirm('撤銷這筆登入連線？該裝置需要重新登入。')) return;
          try {
            setStatus('busy', '撤銷中…');
            await api('/api/auth/sessions/revoke', {id: item.id});
            setStatus('success', '已撤銷該登入連線');
            await refreshSessions();
          } catch (err) {
            setStatus('error', err.message);
          }
        });
        revoke.className = 'btn btn-sm';
      }
    }
  }
  return refreshSessions();
}

async function openMyAccount() {
  const token = ++accountSeq;
  const host = document.getElementById('myAccountPage');
  if (!host) throw new Error('找不到我的帳號頁面');
  const [tree, info, account] = await Promise.all([
    api('/api/library?tree=1').catch(() => ({projects: [], folders: []})),
    api('/api/auth/session'),
    api('/api/auth/account')
  ]);
  if (token !== accountSeq) return;
  accountInfo = account;
  if (info?.bindings) session.bindings = info.bindings;
  if (info?.user) session.user = info.user;
  session.admin = !!info?.admin;
  host.replaceChildren();
  host.classList.add('auth-admin', 'auth-myaccount-dialog');
  const head = element('div', null, host, {className: 'auth-admin-head'});
  element('h2', '我的帳號', head);
  const statusBar = element('div', null, host, {className: 'auth-status-bar'});
  const message = element('p', '', statusBar, {className: 'auth-error'});
  message.setAttribute('role', 'status');
  const setStatus = (type, text) => {
    if (!text) { message.textContent = ''; message.className = 'auth-error'; return; }
    message.className = `auth-error auth-msg-${type}`;
    message.textContent = text;
  };
  const body = element('div', null, host, {className: 'auth-body'});
  const content = element('div', null, body, {className: 'auth-content'});
  await renderMyAccount(content, {setStatus, tree});
}

function auditField(parent, label, type) {
  const wrap = element('label', label, parent, {className: 'auth-audit-field'});
  return element('input', null, wrap, {type});
}

function auditAccount(users, event) {
  const named = users.find(user => user.id === event.actorId)?.username || event.username;
  if (named) return named;
  if (!event.actorId && String(event.action || '').startsWith('auth.login') && event.resource) return event.resource;
  return '系統／未登入';
}

async function openAudit() {
  const token = ++auditSeq;
  const host = document.getElementById('systemAuditPage');
  if (!host) throw new Error('找不到稽核紀錄頁面');
  const admin = await api('/api/auth/admin');
  if (token !== auditSeq) return;
  host.replaceChildren();
  host.classList.add('auth-admin');
  const head = element('div', null, host, {className: 'auth-admin-head'});
  const titleGroup = element('div', null, head, {className: 'auth-admin-title-group'});
  element('h2', '稽核紀錄', titleGroup);
  element('p', '未搜尋時顯示最新 100 筆。搜尋必須指定開始與結束時間。', titleGroup, {className: 'auth-admin-subtitle'});
  const body = element('div', null, host, {className: 'auth-body'});
  const content = element('div', null, body, {className: 'auth-content'});
  const filter = element('form', null, content, {className: 'auth-audit-filter'});
  filter.addEventListener('submit', event => event.preventDefault());
  const accountInput = auditField(filter, '帳號', 'text');
  const fromInput = auditField(filter, '開始時間', 'datetime-local');
  const toInput = auditField(filter, '結束時間', 'datetime-local');
  const ipInput = auditField(filter, 'IP', 'text');
  const actionInput = auditField(filter, '行為', 'text');
  const statusInput = auditField(filter, 'status', 'text');
  const actions = element('div', null, filter, {className: 'auth-audit-actions'});
  const note = element('p', '', content, {className: 'auth-audit-note'});
  note.setAttribute('role', 'status');
  const list = element('div', null, content, {className: 'auth-audit'});
  const paint = payload => {
    list.replaceChildren();
    const items = payload.items || [];
    for (const event of items) {
      const row = element('details', null, list, {className: 'auth-audit-item'});
      const account = auditAccount(admin.users, event);
      const actionLabel = AUDIT_ACTION_LABELS[event.action];
      const actionText = actionLabel && actionLabel !== event.action ? `${event.action} ${actionLabel}` : (event.action || '—');
      const timeStr = new Date(event.at).toLocaleString();
      element('summary', `${timeStr} · ${account} · ${event.address || '—'} · ${actionText} · ${event.status ?? '—'}`, row, {className: 'auth-audit-summary'});
      element('pre', JSON.stringify(event, null, 2), row, {className: 'auth-audit-pre'});
    }
    if (!items.length) element('p', payload.mode === 'search' ? '這段時間沒有符合的紀錄' : '尚無稽核紀錄', list, {className: 'auth-empty'});
    note.textContent = payload.mode === 'search'
      ? (payload.truncated ? `顯示最新 ${items.length} 筆符合結果，請縮小時間或條件` : `找到 ${items.length} 筆`)
      : `顯示最新 ${items.length} 筆`;
  };
  const load = async query => {
    const result = await api('/api/auth/audit' + query);
    if (token !== auditSeq) return;
    paint(result);
  };
  button('搜尋', actions, async () => {
    if (!fromInput.value || !toInput.value) { note.textContent = '請指定開始與結束時間'; return; }
    const from = new Date(fromInput.value), to = new Date(toInput.value);
    if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) || from > to) { note.textContent = '開始時間不可晚於結束時間'; return; }
    to.setSeconds(59, 999);
    const params = new URLSearchParams({from: from.toISOString(), to: to.toISOString()});
    for (const [key, input] of [['account', accountInput], ['ip', ipInput], ['action', actionInput], ['status', statusInput]]) {
      const value = input.value.trim();
      if (value) params.set(key, value);
    }
    try { await load('?' + params); }
    catch (error) { if (token === auditSeq) note.textContent = error.message; }
  });
  button('最新 100 筆', actions, async () => {
    accountInput.value = fromInput.value = toInput.value = ipInput.value = actionInput.value = statusInput.value = '';
    try { await load(''); }
    catch (error) { if (token === auditSeq) note.textContent = error.message; }
  });
  try { await load(''); }
  catch (error) { if (token === auditSeq) note.textContent = error.message; }
}

async function manage() {
  const token = ++manageSeq;
  const host = document.getElementById('authAdminPage');
  if (!host) throw new Error('找不到帳號與權限頁面');
  let [data, tree] = await Promise.all([
    api('/api/auth/admin'),
    api('/api/library?tree=1').catch(() => ({projects: [], folders: []}))
  ]);
  if (token !== manageSeq) return;

  host.replaceChildren();
  host.classList.add('auth-admin');

  const head = element('div', null, host, {className: 'auth-admin-head'});
  const titleGroup = element('div', null, head, {className: 'auth-admin-title-group'});
  element('h2', '帳號與權限', titleGroup);
  element('p', '管理系統登入帳號、角色控制點與授權存取範圍', titleGroup, {className: 'auth-admin-subtitle'});

  const tabs = element('div', null, host, {className: 'auth-tabs'});
  tabs.setAttribute('role', 'tablist');

  const statusBar = element('div', null, host, {className: 'auth-status-bar'});
  const message = element('p', '', statusBar, {className: 'auth-error'});
  message.setAttribute('role', 'status');

  const setStatus = (type, text) => {
    if (!text) {
      message.textContent = '';
      message.className = 'auth-error';
      return;
    }
    message.className = `auth-error auth-msg-${type}`;
    message.textContent = text;
  };

  const body = element('div', null, host, {className: 'auth-body'});
  const content = element('div', null, body, {className: 'auth-content'});

  const run = async (payload, render) => {
    setStatus('busy', '保存中…');
    try {
      data = await api('/api/auth/manage', payload);
      if (token !== manageSeq) return false;
      setStatus('success', '已保存；權限會立即套用至後續請求。');
      render();
      return true;
    } catch (e) {
      setStatus('error', e.message);
      return false;
    }
  };

  const tabButtons = new Map();
  function setActiveTab(name) {
    for (const [label, btn] of tabButtons) {
      const active = label === name;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-selected', active ? 'true' : 'false');
    }
  }

  function users() {
    setActiveTab('帳號管理');
    content.replaceChildren();

    const titleRow = element('div', null, content, {className: 'auth-section-header'});
    element('h3', '帳號管理', titleRow);
    element('span', `${data.users.length} 位帳號 · ${data.users.filter(u => u.enabled).length} 啟用`, titleRow, {className: 'auth-header-badge'});

    const listWrap = element('div', null, content, {className: 'auth-user-list'});
    for (const user of data.users) {
      const row = element('div', null, listWrap, {className: 'auth-row auth-user-card'});

      const avatar = element('div', (user.displayName || user.username).charAt(0).toUpperCase(), row, {className: 'auth-user-avatar'});
      if (user.username === 'admin') avatar.classList.add('is-admin');

      const infoWrap = element('div', null, row, {className: 'auth-user-info'});
      element('span', `${user.username} · ${user.displayName} · ${user.enabled ? '啟用' : '停用'}`, infoWrap, {className: 'auth-user-name-line'});

      const userBindings = data.bindings.filter(b => b.userId === user.id);
      const tagsWrap = element('div', null, infoWrap, {className: 'auth-user-tags'});
      if (user.username === 'admin') {
        element('span', '👑 系統管理者 (全站)', tagsWrap, {className: 'auth-tag auth-tag-admin'});
      }

      const projectBindings = userBindings.filter(b => b.scope.type === 'project');
      const otherBindings = userBindings.filter(b => b.scope.type !== 'project');

      for (const b of projectBindings) {
        const rName = data.roles.find(r => r.id === b.roleId)?.name || b.roleId;
        const pName = tree.projects?.find(p => p.id === b.scope.id)?.name || b.scope.id;
        element('span', `🏢 ${pName}：${rName}`, tagsWrap, {className: 'auth-tag auth-tag-project-linked'});
      }
      for (const b of otherBindings) {
        const rName = data.roles.find(r => r.id === b.roleId)?.name || b.roleId;
        const locName = b.scope.type === 'folder'
          ? (tree.folders?.find(f => f.id === b.scope.id)?.name || b.scope.id)
          : b.scope.type === 'global' ? '全站' : '未分類';
        element('span', `${rName} · ${locName}`, tagsWrap, {className: 'auth-tag auth-tag-role'});
      }
      if (!userBindings.length && user.username !== 'admin') {
        element('span', '尚未連結專案或指派角色', tagsWrap, {className: 'auth-tag auth-tag-none'});
      }

      const actions = element('div', null, row, {className: 'auth-user-actions'});
      const projectLinkBtn = button('專案連結', actions, () => bindings(user.id).catch(e => setStatus('error', e.message)));
      projectLinkBtn.className = 'btn btn-sm btn-primary';

      const bindBtn = button('角色指派', actions, () => bindings(user.id).catch(e => setStatus('error', e.message)));
      bindBtn.className = 'btn btn-sm';

      const editBtn = button('修改帳號', actions, () => {
        const edit = element('dialog', null, document.body, {className: 'auth-admin auth-dialog-sub'});
        const editHead = element('div', null, edit, {className: 'auth-admin-head'});
        element('h3', `修改帳號：${user.username}`, editHead);
        button('關閉', editHead, () => edit.close());
        const form = element('form', null, edit, {className: 'auth-form'});
        const username = field(form, '帳號', 'text', user.username);
        const displayName = field(form, '顯示名稱', 'text', user.displayName);
        username.readOnly = user.username === 'admin';
        const btnRow = element('div', null, form, {className: 'auth-form-btn-row'});
        element('button', '保存修改', btnRow, {type: 'submit', className: 'btn btn-primary'});
        button('取消', btnRow, () => edit.close());
        const hint = element('p', '', form, {className: 'auth-error'});
        form.onsubmit = async e => {
          e.preventDefault();
          if (await run({operation: 'user.update', id: user.id, username: username.value, displayName: displayName.value}, users)) {
            edit.close();
          } else {
            hint.className = 'auth-error auth-msg-error';
            hint.textContent = message.textContent;
          }
        };
        edit.onclose = () => edit.remove();
        edit.showModal();
      });
      editBtn.className = 'btn btn-sm';

      const resetBtn = button('重設密碼', actions, () => {

        const reset = element('dialog', null, document.body, {className: 'auth-admin auth-dialog-sub'});
        const resetHead = element('div', null, reset, {className: 'auth-admin-head'});
        element('h3', `重設 ${user.username} 的密碼`, resetHead);
        button('關閉', resetHead, () => reset.close());
        const form = element('form', null, reset, {className: 'auth-form'});
        element('p', '新密碼需為 6 至 256 字元。重設完成後該帳號之現有登入連線將全數撤銷。', form, {className: 'auth-form-desc'});
        const password = field(form, '新密碼（6–256 字元）', 'password');
        password.minLength = 6;
        password.maxLength = 256;
        password.autocomplete = 'new-password';
        const btnRow = element('div', null, form, {className: 'auth-form-btn-row'});
        element('button', '保存密碼', btnRow, {type: 'submit', className: 'btn btn-primary'});
        button('取消', btnRow, () => reset.close());
        const hint = element('p', '', form, {className: 'auth-error'});
        form.onsubmit = async e => {
          e.preventDefault();
          const saved = await run({operation: 'user.update', id: user.id, password: password.value}, users);
          if (saved) {
            reset.close();
            if (user.id === session.user.id) location.reload();
          } else {
            hint.className = 'auth-error auth-msg-error';
            hint.textContent = message.textContent;
          }
        };
        reset.onclose = () => reset.remove();
        reset.showModal();
        password.focus();
      });
      resetBtn.className = 'btn btn-sm';

      if (user.username !== 'admin') {
        const toggleBtn = button(user.enabled ? '停用' : '啟用', actions, () =>
          run({operation: 'user.update', id: user.id, enabled: !user.enabled}, users)
        );
        toggleBtn.className = 'btn btn-sm' + (user.enabled ? ' btn-warn-outline' : ' btn-ok-outline');

        const deleteBtn = button('刪除帳號', actions, () => {
          if (confirm(`刪除帳號「${user.username}」？登入與角色指派將立即撤銷，既有文件、作者及稽核紀錄會保留。`)) {
            run({operation: 'user.delete', id: user.id}, users);
          }
        });
        deleteBtn.className = 'btn btn-sm btn-danger';
      }
    }

    // Add User Form Card
    const addCard = element('div', null, content, {className: 'auth-section-card'});
    const form = element('form', null, addCard, {className: 'auth-form'});
    element('h3', '新增帳號', form);
    element('p', '建立新的本機帳號。初始密碼長度需在 6 至 256 字元之間。', form, {className: 'auth-form-desc'});
    const grid = element('div', null, form, {className: 'auth-form-grid'});
    const username = field(grid, '帳號');
    const displayName = field(grid, '顯示名稱');
    const password = field(grid, '初始密碼', 'password');
    password.minLength = 6;
    password.maxLength = 256;
    password.autocomplete = 'new-password';
    element('button', '新增帳號', form, {type: 'submit', className: 'btn btn-primary'});
    form.onsubmit = e => {
      e.preventDefault();
      run({
        operation: 'user.create',
        username: username.value,
        displayName: displayName.value,
        password: password.value
      }, users);
    };
  }

  function roles() {
    setActiveTab('角色管理');
    content.replaceChildren();

    const roleHead = element('div', null, content, {className: 'auth-section-header'});
    element('h3', '角色管理', roleHead);
    element('span', `${data.roles.length} 個角色`, roleHead, {className: 'auth-header-badge'});

    const toolbar = element('div', null, content, {className: 'auth-role-toolbar'});
    const pickerWrap = element('div', null, toolbar, {className: 'auth-role-picker-wrap'});
    const picker = select(pickerWrap, '角色', [
      ['', '新增角色'],
      ...data.roles.filter(r => !['system-admin','project-manager'].includes(r.id)).map(r => [r.id, r.name])
    ]);

    const removeRole = button('刪除角色', toolbar, () => {
      const role = data.roles.find(r => r.id === picker.value);
      if (role && confirm(`刪除角色「${role.name}」？對應的 ${data.bindings.filter(b => b.roleId === role.id).length} 筆角色指派會一併移除並立即撤權。`)) {
        run({operation: 'role.delete', id: role.id}, roles);
      }
    });
    removeRole.disabled = true;
    removeRole.className = 'btn btn-danger';

    const form = element('form', null, content, {className: 'auth-form'});
    const nameWrap = element('div', null, form, {className: 'auth-role-name-row'});
    const name = field(nameWrap, '角色名稱');

    element('p', '勾選控制項就會帶入相關功能，不必再逐項勾底層控制點。釋疑、標註與書籤可限制只改本人的內容；未記錄作者的歷史資料不算本人。', form, {className: 'auth-form-hint'});

    const points = element('div', null, form, {className: 'auth-permissions'});
    const inputs = new Map();
    const groups = [];
    for (const control of CONTROLS) {
      if (groups.at(-1) !== control.group) groups.push(control.group);
    }
    for (const group of groups) {
      const groupCard = element('div', null, points, {className: 'auth-perm-group'});
      const gHead = element('div', null, groupCard, {className: 'auth-perm-group-head'});
      const titleDiv = element('div', null, gHead, {className: 'auth-perm-group-title'});
      element('strong', group, titleDiv);
      const grid = element('div', null, groupCard, {className: 'auth-perm-grid'});
      for (const control of CONTROLS.filter(item => item.group === group)) {
        const row = element('label', null, grid, {className: 'auth-perm-item'});
        const check = element('input', null, row, {type: 'checkbox'});
        check.setAttribute('aria-label', control.title);
        const textWrap = element('div', null, row, {className: 'auth-perm-text'});
        element('span', control.title, textWrap, {className: 'auth-perm-label'});
        element('span', control.desc, textWrap, {className: 'auth-perm-code'});
        let ownership = {value: 'any'};
        if (control.ownership) {
          const ownershipWrap = element('div', null, row, {className: 'auth-ownership-wrap is-supported'});
          ownership = select(ownershipWrap, `${control.title}的範圍`, [['any', '所有人'], ['own', '本人']]);
          element('span', '可設本人條件', ownershipWrap, {className: 'auth-ownership-hint'});
        }
        inputs.set(control.id, {check, ownership, control});
      }
    }

    const applyRole = role => {
      const actions = new Set((role?.permissions || []).map(item => item.action));
      for (const [id, {check, ownership, control}] of inputs) {
        check.checked = control.actions.some(action => actions.has(action));
        const owned = (role?.permissions || []).find(item => control.actions.includes(item.action) && item.ownership === 'own');
        ownership.value = owned ? 'own' : 'any';
        if (!role) check.checked = false;
      }
      if (!role) name.value = '';
    };

    picker.onchange = () => {
      removeRole.disabled = !picker.value;
      const role = data.roles.find(r => r.id === picker.value);
      name.value = role?.name || '';
      applyRole(role);
    };

    element('button', '保存角色', form, {type: 'submit', className: 'btn btn-primary'});
    form.onsubmit = e => {
      e.preventDefault();
      run({
        operation: 'role.save',
        id: picker.value || undefined,
        name: name.value,
        permissions: [...inputs].filter(([, v]) => v.check.checked).map(([control, v]) => ({
          control,
          ownership: v.ownership.value
        }))
      }, roles);
    };
  }

  async function bindings(userId) {
    content.replaceChildren();

    const navHead = element('div', null, content, {className: 'auth-nav-header'});
    const backBtn = button('返回帳號管理', navHead, users);
    backBtn.className = 'btn btn-secondary';
    element('h3', '角色指派與資料範圍', navHead);

    const targetUser = data.users.find(u => u.id === userId);
    if (targetUser) {
      const banner = element('div', null, content, {className: 'auth-user-target-banner'});
      banner.innerHTML = `<span>管理帳號：<strong>${targetUser.displayName}</strong> <code>@${targetUser.username}</code></span>`;
    }

    const callout = element('div', null, content, {className: 'auth-concept-callout'});
    callout.innerHTML = `<span class="auth-callout-icon">💡</span><div><strong>專案連結機制：</strong>連結專案後，該帳號就能看到這個專案目錄與其中的文件清單。其他操作仍依角色權限。同一個角色指派到不同專案時，彼此權限各自獨立；未連結的專案無法查閱。</div>`;

    const pending=(await api('/api/auth/admin')).orphanLinks;
    if(pending&&(pending.bindings.length||pending.groups.length||pending.policies.length)){
      const panel=element('div',null,content,{className:'auth-section-card'});
      element('h4','失效專案／目錄連結（全站）',panel);
      element('p',`角色指派 ${pending.bindings.length} 筆、釋疑群組 ${pending.groups.length} 筆、專案角色政策 ${pending.policies.length} 筆。`,panel);
      for(const b of pending.bindings){
        const user=data.users.find(u=>u.id===b.userId);
        element('p',`${user?.username||b.userId} → ${data.roles.find(r=>r.id===b.roleId)?.name||b.roleId} → ${b.scope.type==='project'?'專案':'目錄'} ${b.scope.id}`,panel);
      }
      for(const g of pending.groups)element('p',`釋疑群組：${g.name}（專案 ${g.projectId}）`,panel);
      for(const p of pending.policies)element('p',`角色政策：專案 ${p.projectId}`,panel);
      button('清理以上失效連結',panel,()=>{
        if(confirm('清理以上全站失效連結？帳號、角色定義與歷史釋疑會保留。'))return run({operation:'project.links.cleanup',token:pending.token},()=>bindings(userId));
      });
    }

    const rerender = () => bindings(userId);
    const currentTree = await api('/api/library?tree=1').catch(() => tree);
    const customRoles = data.roles.filter(r => !['system-admin','project-manager'].includes(r.id));

    // Section 1: 專案連結矩陣
    const projectSection = element('div', null, content, {className: 'auth-section-card'});
    element('h4', '🏢 專案連結與角色指派', projectSection);
    element('p', '每個帳號在每個專案只能設定一個業務角色；專案管理者任命獨立設定，不佔用業務角色名額。', projectSection, {className: 'auth-form-desc'});

    if (!currentTree.projects?.length) {
      element('p', '目前圖庫中尚未建立專案。請先在文件清冊建立專案後進行連結。', projectSection, {className: 'auth-empty-hint'});
    } else {
      const pTableWrap = element('div', null, projectSection, {className: 'auth-project-table-wrap'});
      const table = element('table', null, pTableWrap, {className: 'auth-project-table'});
      const thead = element('thead', null, table);
      const trHead = element('tr', null, thead);
      element('th', '專案名稱', trHead);
      element('th', '業務角色狀態', trHead);
      element('th', '業務角色', trHead);
      element('th', '專案管理者', trHead);

      const tbody = element('tbody', null, table);

      for (const proj of currentTree.projects) {
        const tr = element('tr', null, tbody);

        // 專案名稱
        const tdProj = element('td', null, tr);
        element('span', '🏢 ' + proj.name, tdProj, {className: 'auth-project-name'});

        // 該專案之指派
        const projBindings = data.bindings.filter(b =>
          (!userId || b.userId === userId) &&
          b.scope.type === 'project' &&
          b.scope.id === proj.id
        );
        const isLinked = projBindings.some(b=>!['system-admin','project-manager'].includes(b.roleId));

        // 連結狀態
        const tdStatus = element('td', null, tr);
        if (isLinked) {
          element('span', '已設定', tdStatus, {className: 'auth-tag auth-tag-active'});
        } else {
          element('span', '未設定', tdStatus, {className: 'auth-tag auth-tag-none'});
        }

        // Business bindings and manager appointments are independent records.
        const businessBindings=projBindings.filter(b=>b.roleId!=='project-manager'&&b.roleId!=='system-admin');
        const tdRole=element('td',null,tr);
        const rolePicker=(parent,label,value)=>{
          const select=element('select',null,parent,{className:'auth-project-role-select'});
          select.setAttribute('aria-label',label);
          for(const role of customRoles)element('option',role.name,select,{value:role.id});
          if(value)select.value=value;
          return select;
        };
        for(const [index,binding] of businessBindings.entries()){
          const row=element('div',null,tdRole,{className:'auth-project-role-row'});
          const select=rolePicker(row,`專案 ${proj.name} 業務角色 ${index+1}`,binding.roleId);
          button('變更角色',row,()=>{
            if(select.value===binding.roleId){setStatus('busy','角色未更動');return;}
            return run({operation:'binding.update',id:binding.id,roleId:select.value,expectedRoleId:binding.roleId},rerender);
          });
          button('解除此角色',row,()=>{
            if(confirm('解除這筆業務角色？專案管理者任命及其他角色會保留。'))return run({operation:'binding.delete',id:binding.id},rerender);
          });
        }
        if(!businessBindings.length){
          const addRow=element('div',null,tdRole,{className:'auth-project-role-row'});
          const addSelect=rolePicker(addRow,`專案 ${proj.name} 新增業務角色`);
          const add=button('連結專案',addRow,()=>run({operation:'binding.create',userId:userId||data.users[0]?.id,roleId:addSelect.value,scope:{type:'project',id:proj.id}},rerender));
          add.disabled=!customRoles.length;
          if(!customRoles.length)element('small','請先建立業務角色',tdRole);
        }
        const managerCell=element('td',null,tr,{className:'auth-project-manager'});
        const managers=projBindings.filter(b=>b.roleId==='project-manager');
        element('span',managers.length?'已任命':'未任命',managerCell,{className:'auth-tag'});
        if(managers.length){
          for(const binding of managers)button('解除任命',managerCell,()=>{
            if(confirm('解除專案管理者任命？原有業務角色會保留。'))return run({operation:'binding.delete',id:binding.id},rerender);
          });
        }else button('任命專案管理者',managerCell,()=>run({operation:'binding.create',userId:userId||data.users[0]?.id,roleId:'project-manager',scope:{type:'project',id:proj.id}},rerender));

      }
    }

    // Section 2: 所有已生效指派清單
    const listWrap = element('div', null, content, {className: 'auth-section-card'});
    element('h4', '📋 所有角色指派清單', listWrap);
    const bList = element('div', null, listWrap, {className: 'auth-binding-list'});
    const userBindings = data.bindings.filter(b => !userId || b.userId === userId);
    if (!userBindings.length) {
      element('p', '此帳號目前沒有任何角色指派。請在上方設定專案連結或在下方新增自訂範圍。', bList, {className: 'auth-empty-hint'});
    }
    for (const b of userBindings) {
      const name = data.users.find(u => u.id === b.userId)?.username || b.userId;
      const role = data.roles.find(r => r.id === b.roleId)?.name || b.roleId;
      const location = b.scope.type === 'project'
        ? (currentTree.projects?.find(p => p.id === b.scope.id)?.name || b.scope.id)
        : b.scope.type === 'folder'
          ? (currentTree.folders?.find(f => f.id === b.scope.id)?.name || b.scope.id)
          : b.scope.type === 'global' ? '全站' : '未分類';
      const invalid=pending?.bindings.some(entry=>entry.id===b.id);
      const row = element('div', null, bList, {className: 'auth-row auth-binding-card'});
      element('span', `${name} → ${role} → ${location || '已刪除範圍'}${invalid?'（失效連結）':''}`, row, {className: 'auth-binding-summary'});
      if (b.roleId !== 'system-admin') {
        const rm = button('移除', row, () => run({operation: 'binding.delete', id: b.id}, rerender));
        rm.className = 'btn btn-sm btn-danger';
      }
    }

    // Section 3: 進階指派 (子目錄 / 全站 / 未分類)
    const addCard = element('div', null, content, {className: 'auth-section-card'});
    const form = element('form', null, addCard, {className: 'auth-form'});
    element('h4', '➕ 進階自訂指派（子目錄 / 全站 / 未分類）', form);
    const formGrid = element('div', null, form, {className: 'auth-form-grid'});
    const user = select(formGrid, '帳號', data.users.map(u => [u.id, `${u.username} (${u.displayName})`]));
    const role = select(formGrid, '角色', data.roles.filter(r => !['system-admin','project-manager'].includes(r.id)).map(r => [r.id, r.name]));
    if (userId) {
      user.value = userId;
      user.disabled = true;
    }
    const scope = select(formGrid, '資料範圍', [
      ['global', '🌐 全站（所有專案與目錄）'],
      ['unassigned', '📂 未分類文件'],
      ...currentTree.projects.map(p => [`project:${p.id}`, `🏢 專案：${p.name}`]),
      ...currentTree.folders.map(f => [`folder:${f.id}`, `📁 目錄：${currentTree.projects.find(p => p.id === f.projectId)?.name || ''} / ${f.name}`])
    ]);
    element('button', '新增指派', form, {type: 'submit', className: 'btn btn-primary'});
    form.onsubmit = e => {
      e.preventDefault();
      const [type, id] = scope.value.split(':');
      run({
        operation: 'binding.create',
        userId: user.value,
        roleId: role.value,
        scope: {type, ...(id ? {id} : {})}
      }, rerender);
    };
  }


  function lockedLogins() {
    setActiveTab('登入鎖定');
    content.replaceChildren();
    const titleRow = element('div', null, content, {className: 'auth-section-header'});
    element('h3', '登入鎖定', titleRow);
    element('p', '同一登入位址失敗超過 6 次後鎖定為不允許登入，只有這裡可以解鎖。帳號連續失敗 3 次會暫停 15 分鐘，時間到會自己恢復。', content, {className: 'auth-form-desc'});
    const rows = data.lockedAddresses || [];
    if (!rows.length) {
      element('p', '目前沒有鎖定的登入位址', content, {className: 'auth-form-desc'});
      return;
    }
    const listWrap = element('div', null, content, {className: 'auth-user-list'});
    for (const row of rows) {
      const card = element('div', null, listWrap, {className: 'auth-row auth-user-card'});
      const info = element('div', null, card, {className: 'auth-user-info'});
      element('span', `${row.address} · 不允許登入`, info, {className: 'auth-user-name-line'});
      const when = row.lockedAt ? new Date(row.lockedAt).toLocaleString() : '';
      element('span', `失敗 ${row.failures} 次${when ? ` · ${when}` : ''}`, info, {className: 'auth-tag auth-tag-none'});
      const actions = element('div', null, card, {className: 'auth-user-actions'});
      const unlock = button('解鎖', actions, () => run({operation: 'login.address.unlock', address: row.address}, lockedLogins));
      unlock.className = 'btn btn-sm btn-primary';
    }
  }

  for (const [label, fn] of [['帳號管理', users], ['角色管理', roles], ['登入鎖定', lockedLogins]]) {
    const b = button(label, tabs, () => {
      setStatus('busy', '');
      Promise.resolve(fn()).catch(e => setStatus('error', e.message));
    });
    b.className = 'auth-tab-btn';
    tabButtons.set(label, b);
  }

  users();
}
